import React, { FC, useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import {
  OrganizationLabel,
  OrganizationLabelPayload,
} from "@/domain/organizationLabels.interface";
import organizationLabelValidationSchema, {
  OrganizationLabelFormData,
} from "./validation.schema";

type OrganizationLabelModalProps = {
  open: boolean;
  onClose: () => void;
  organizationLabel?: OrganizationLabel;
  onSave: (data: OrganizationLabelPayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: OrganizationLabelFormData = {
  name: "",
  description: "",
  status: "ACTIVE",
};

const OrganizationLabelModal: FC<OrganizationLabelModalProps> = ({
  open,
  onClose,
  organizationLabel,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!organizationLabel;

  const methods = useForm<OrganizationLabelFormData>({
    resolver: yupResolver(organizationLabelValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (organizationLabel) {
      reset({
        name: organizationLabel.name,
        description: organizationLabel.description,
        status: organizationLabel.status,
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [organizationLabel, reset]);

  const onSubmit = async (data: OrganizationLabelFormData) => {
    const currentUserId = userInfo?._id as string;
    await onSave({
      name: data.name.trim(),
      description: data.description.trim(),
      status: data.status as OrganizationLabelPayload["status"],
      ...(isEdit
        ? { updatedBy: currentUserId }
        : { createdBy: currentUserId }),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Organization Label" : "New Organization Label"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 320,
        maxHeight: 480,
        maxWidth: 640,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="organization-label-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="organization-label-description"
                      label="Description"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.description}
                      helperText={
                        errors.description ? errors.description.message : null
                      }
                      multiline
                      minRows={3}
                      maxRows={6}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="organization-label-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default OrganizationLabelModal;
